import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import toast from "react-hot-toast";

export default function PollResultModal ({ open, onClose, poll }) {
    const [pollData, setPollData] = useState(null);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        if (!open || !poll) {
            setPollData(null);
            return;
        }

        const fetchPollDetails = async () => {
            try {
                setLoading(true);
                const token = Cookies.get("auth_token");
                if (!token) {
                    toast.error("Not authenticated");
                    return;
                }

                const params = new URLSearchParams();
                params.append("group_id", poll.original?.group_id || poll.group_id);
                params.append("poll_id", poll.original?.poll?.poll_id || poll.poll_id || poll.id);

                const res = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/group/poll/details`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/x-www-form-urlencoded"
                        },
                        body: params.toString()
                    }
                );

                const data = await res.json();

                if (data.data && Array.isArray(data.data) && data.data[0]?.poll) {
                    setPollData(data.data[0].poll);
                } else {
                    toast.error("Failed to fetch poll details");
                }
            } catch (err) {
                toast.error("Failed to fetch poll details");
            } finally {
                setLoading(false);
            }
        };

        fetchPollDetails();
    }, [open, poll]);
    if (!open || !poll) return null;

    const displayData = pollData || poll;

    // Compute total votes and make robust numeric parsing
    const totalVotes = Array.isArray(displayData?.options)
        ? displayData.options.reduce((sum, option) => {
            const n = Number(option?.count ?? option?.votes ?? 0) || 0;
            return sum + n;
          }, 0)
        : 0;

    const formatOptionLabel = (option) => option?.text ?? option?.title ?? option?.label ?? option?.name ?? String(option ?? '');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Poll Votes</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="space-y-4">
                    {Array.isArray(displayData.options) && displayData.options.map((option, index) => {
                        const voteCount = Number(option?.count ?? option?.votes ?? 0) || 0;
                        const percentageFloat = totalVotes > 0 ? (voteCount / totalVotes) * 100 : 0;
                        const percentageDisplay = totalVotes > 0 ? `${percentageFloat.toFixed(1)}%` : "0%";
                        const percentageWidth = Math.max(0, Math.min(100, percentageFloat));

                        return (
                            <div key={index} className="border-b border-gray-200 pb-3 last:border-b-0">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-medium">{formatOptionLabel(option)}</span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm text-gray-500">{voteCount} votes</span>
                                        <span className="text-sm text-gray-500">{percentageDisplay}</span>
                                    </div>
                                </div>

                                {/* Progress bar */}
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div
                                        className="bg-purple-500 h-2 rounded-full"
                                        style={{ width: `${percentageWidth}%` }}
                                    />
                                </div>

                                {/* Voter list */}
                                <div className="text-sm text-gray-500 mt-2">
                                    {option.voters && option.voters.length > 0 ? (
                                        option.voters.map((voter, i) => (
                                            <div key={i} className="flex gap-5 items-center mb-2">
                                                <img src={voter.profile_image_url ? (voter.profile_image_url.startsWith('http') ? voter.profile_image_url : `${process.env.REACT_APP_API_BASE_URL}${voter.profile_image_url}`) : "../chatscreen/profile.svg"} alt={voter.name} className="h-12 w-12 rounded-full object-cover" />
                                                <div className="min-w-0 flex-1 text-left">
                                                    <p>{voter.name}</p>
                                                    <p className="truncate text-xs">{voter.phone_number}</p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-xs text-gray-400">No votes yet</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                    <p className="text-sm text-gray-500">
                        {totalVotes} total votes
                    </p>
                </div>
            </div>
        </div>
    );
};